import React, { useState } from "react";

import {
  BulkImportRequestFragmentFragment,
  useSendBulkInvitationEmailsMutation,
} from "~/components/Admin/User/BulkImport/BulkImport.generated";
import { PrimaryButton, SecondaryButton } from "~/components/common/buttons";

interface Props {
  request: BulkImportRequestFragmentFragment;
  onSent: (updatedRequest: BulkImportRequestFragmentFragment) => void;
  onSkip: () => void;
}

export const SendInvitationsStep = ({ request, onSent, onSkip }: Props) => {
  const newUserRows = request.rows.filter((r) => r.outcome === "NEW_USER");
  const [selected, setSelected] = useState<Set<string>>(
    new Set(newUserRows.map((r) => r.id))
  );
  const [sending, setSending] = useState(false);

  const [sendBulkInvitationEmails] = useSendBulkInvitationEmailsMutation();

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const result = await sendBulkInvitationEmails({
        variables: {
          requestId: request.id,
          userRowIds: Array.from(selected),
        },
      });
      const updated = result.data?.sendBulkInvitationEmails?.request;
      if (updated) onSent(updated);
    } finally {
      setSending(false);
    }
  };

  if (newUserRows.length === 0) {
    return (
      <div>
        <p>No new users to invite in this import.</p>
        <PrimaryButton onClick={onSkip}>Done</PrimaryButton>
      </div>
    );
  }

  return (
    <div>
      <h5 className="mb-3">Send Invitation Emails</h5>
      <p>
        Select the new users to send invitation emails to. Existing users are
        excluded.
      </p>
      <table className="table table-sm table-bordered mb-3">
        <thead>
          <tr>
            <th style={{ width: 40 }}>
              <input
                type="checkbox"
                checked={selected.size === newUserRows.length}
                onChange={(e) =>
                  setSelected(
                    e.target.checked
                      ? new Set(newUserRows.map((r) => r.id))
                      : new Set()
                  )
                }
              />
            </th>
            <th>Name</th>
            <th>Email</th>
            <th>Group</th>
          </tr>
        </thead>
        <tbody>
          {newUserRows.map((row) => (
            <tr key={row.id}>
              <td>
                <input
                  type="checkbox"
                  checked={selected.has(row.id)}
                  onChange={() => toggle(row.id)}
                />
              </td>
              <td>
                {row.firstName} {row.lastName}
              </td>
              <td>{row.email}</td>
              <td>{row.userGroupName ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="d-flex justify-content-between">
        <SecondaryButton onClick={onSkip}>Skip</SecondaryButton>
        <PrimaryButton
          onClick={handleSend}
          disabled={selected.size === 0 || sending}
        >
          {sending
            ? "Sending…"
            : `Send ${selected.size} invitation${
                selected.size !== 1 ? "s" : ""
              }`}
        </PrimaryButton>
      </div>
    </div>
  );
};
